import { config } from '../config';
import { NESTED_FIELD_ORDERS } from './fields';

/**
 * **Canonical JSON — the content half of INV-12.**
 *
 * `JSON.stringify(row)` is not a serializer for a byte-identity requirement. Three of its defaults
 * are nondeterministic against the data this product holds, and each one is silent:
 *
 * | Default | What it does to the bytes |
 * |---|---|
 * | key order is the object's own property order | V8 emits integer-like keys first and then insertion order, so a row read from Dexie, a row rebuilt by a spread, and a row parsed from an import can serialize their fields in three different sequences for identical data |
 * | `undefined` properties are **dropped** | one absent field silently changes the key set, so two rows of the same entity produce different shapes and a diff points at the wrong place |
 * | `NaN` / `Infinity` become `null` | a corrupt value is laundered into a legal one with no record that it happened |
 *
 * So key order is **declared**, never observed (`fields.ts`); a declared field that is missing is
 * emitted as an explicit `null`; and every substitution is recorded in `CanonicalIssues` so the
 * caller can log it (OBS-1). Nothing here throws: §13.5's recovery export has to succeed on a
 * profile that is already damaged, and an export that refuses to run is the one failure mode that
 * loses the data outright.
 *
 * **Output is indented, deliberately.** Two-space indent with one row per line costs a few percent
 * of a payload that is dominated by audio (§11.6), and buys two things worth more than that: BAK-1
 * is "the migration path off the platform", so a human unzipping it should be able to read it; and
 * when an INV-12 determinism test fails, a byte diff over a pretty file names the row and field,
 * while a diff over one 2 MB line names an offset. The layout is fixed and rule-driven, so it is
 * exactly as deterministic as a compact one.
 */

/** Everything the writer substituted or adjusted, by JSON path. Never affects the bytes' shape. */
export interface CanonicalIssues {
  /**
   * Paths whose non-integer number was outside `GEOMETRY_DECIMALS`.
   *
   * U-3 rounds geometry at the repo boundary, so in a healthy profile this is always empty. The
   * check is here anyway because INV-12 is *this* layer's invariant: a value that reached disk
   * around the repo boundary must not be able to make the archive engine-dependent.
   */
  rounded: string[];
  /** Paths where a value could not be represented and `null` was written instead, with the reason. */
  coerced: string[];
}

export function emptyIssues(): CanonicalIssues {
  return { rounded: [], coerced: [] };
}

const INDENT = '  ';

/** Rounds through `toFixed`, whose output is fully specified, rather than through `*10^d` scaling. */
function roundToDecimals(value: number, decimals: number): number {
  return Number(value.toFixed(decimals));
}

function writeNumber(value: number, path: string, issues: CanonicalIssues): string {
  if (!Number.isFinite(value)) {
    issues.coerced.push(`${path}: ${String(value)} is not representable in JSON`);
    return 'null';
  }
  // `-0` and `0` are `===` but `String(-0)` is `'0'` while `JSON.stringify(-0)` is also `'0'`; the
  // normalisation is here so the rule is stated rather than inherited from a coincidence.
  if (Object.is(value, -0)) return '0';
  if (Number.isInteger(value)) return String(value);

  const rounded = roundToDecimals(value, config.GEOMETRY_DECIMALS);
  if (rounded !== value) issues.rounded.push(path);
  return String(rounded);
}

/**
 * Keys for an object the writer has no declaration for — `Setting.value`, and any JSON that arrives
 * from an import. Sorted by UTF-16 code unit, which is `Array.prototype.sort`'s default and is the
 * one ordering that does not depend on the object's history.
 */
function sortedKeys(obj: Record<string, unknown>): string[] {
  return Object.keys(obj).sort();
}

function writeValue(
  value: unknown,
  fieldName: string | null,
  path: string,
  depth: number,
  issues: CanonicalIssues,
): string {
  if (value === null || value === undefined) return 'null';

  switch (typeof value) {
    case 'boolean':
      return value ? 'true' : 'false';
    case 'number':
      return writeNumber(value, path, issues);
    case 'string':
      return JSON.stringify(value);
    case 'bigint':
      issues.coerced.push(`${path}: bigint is not a schema type`);
      return 'null';
    case 'function':
    case 'symbol':
      issues.coerced.push(`${path}: ${typeof value} is not serialisable`);
      return 'null';
    default:
      break;
  }

  // §11.7 — "integer milliseconds for all times". A `Date` on a row means something wrote one
  // instead of an epoch; keeping the instant is strictly better than dropping it, and the
  // substitution is recorded so the writing code path can be found.
  if (value instanceof Date) {
    issues.coerced.push(`${path}: Date coerced to integer milliseconds`);
    return String(value.getTime());
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    const pad = INDENT.repeat(depth + 1);
    const items = value.map(
      (item, i) => `${pad}${writeValue(item, fieldName, `${path}[${i}]`, depth + 1, issues)}`,
    );
    return `[\n${items.join(',\n')}\n${INDENT.repeat(depth)}]`;
  }

  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const declared = fieldName === null ? undefined : NESTED_FIELD_ORDERS[fieldName];
    // A declared nested shape emits its declared keys **that are present**: `SetPieceFlags` and
    // `TemplateEntry.slots` are genuinely optional, and writing `swing: null` would assert a fact
    // ("no swing") that the absent key does not assert. Presence is data; only order is imposed.
    const keys = declared
      ? declared.filter((key) => key in obj && obj[key] !== undefined)
      : sortedKeys(obj);
    return writeKeyedObject(obj, keys, path, depth, issues, false);
  }

  issues.coerced.push(`${path}: unsupported value`);
  return 'null';
}

function writeKeyedObject(
  obj: Record<string, unknown>,
  keys: readonly string[],
  path: string,
  depth: number,
  issues: CanonicalIssues,
  nullMissing: boolean,
): string {
  if (keys.length === 0) return '{}';
  const pad = INDENT.repeat(depth + 1);
  const parts = keys.map((key) => {
    const childPath = path === '' ? key : `${path}.${key}`;
    if (nullMissing && !(key in obj)) {
      issues.coerced.push(`${childPath}: declared field absent from the row`);
      return `${pad}${JSON.stringify(key)}: null`;
    }
    if (nullMissing && obj[key] === undefined) {
      issues.coerced.push(`${childPath}: declared field was undefined`);
      return `${pad}${JSON.stringify(key)}: null`;
    }
    return `${pad}${JSON.stringify(key)}: ${writeValue(obj[key], key, childPath, depth + 1, issues)}`;
  });
  return `{\n${parts.join(',\n')}\n${INDENT.repeat(depth)}}`;
}

/**
 * One entity row: **every declared field, in declared order**, with a missing or `undefined` field
 * written as an explicit `null`.
 *
 * The difference from a nested shape is deliberate. A row's field set is fixed by its type, so a
 * missing field is damage and the archive should say so at a stable position; a nested optional key
 * is data, and inventing a `null` for it would change its meaning.
 */
export function canonicalRow(
  row: unknown,
  fields: readonly string[],
  path: string,
  depth: number,
  issues: CanonicalIssues,
): string {
  if (row === null || typeof row !== 'object') {
    issues.coerced.push(`${path}: row is not an object`);
    return 'null';
  }
  return writeKeyedObject(row as Record<string, unknown>, fields, path, depth, issues, true);
}

/**
 * A whole document from an ordered list of `(key, alreadySerialized)` pairs.
 *
 * Takes pre-serialized members so `serialize.ts` can build the entity arrays row by row and stream
 * `manifest.json` through the same layout rules, with one definition of what a canonical object
 * looks like. Always ends with a trailing newline — POSIX text, and it makes a `tail` of the file
 * inside a zip readable.
 */
export function canonicalDocument(members: readonly (readonly [string, string])[]): string {
  if (members.length === 0) return '{}\n';
  const parts = members.map(([key, body]) => `${INDENT}${JSON.stringify(key)}: ${body}`);
  return `{\n${parts.join(',\n')}\n}\n`;
}

/** An array of pre-serialized rows, one per line, at `depth`. */
export function canonicalArray(items: readonly string[], depth: number): string {
  if (items.length === 0) return '[]';
  const pad = INDENT.repeat(depth + 1);
  return `[\n${items.map((item) => `${pad}${item}`).join(',\n')}\n${INDENT.repeat(depth)}]`;
}

/** A plain value at `depth` — used for `manifest.json`'s scalars and its counts object. */
export function canonicalScalar(value: unknown, depth: number, issues: CanonicalIssues): string {
  return writeValue(value, null, '', depth, issues);
}

/**
 * An object whose keys are supplied in a declared order — `manifest.entityCounts`, whose order is
 * `ARCHIVE_ENTITIES` rather than alphabetical so it reads in the same sequence as `data.json`.
 */
export function canonicalOrderedObject(
  entries: readonly (readonly [string, unknown])[],
  path: string,
  depth: number,
  issues: CanonicalIssues,
): string {
  const obj: Record<string, unknown> = {};
  for (const [key, value] of entries) obj[key] = value;
  return writeKeyedObject(
    obj,
    entries.map(([key]) => key),
    path,
    depth,
    issues,
    true,
  );
}
